import { getBootstrap } from './api';

export type MiniappTab = 'tokens' | 'fees' | 'wallet';

function runIdle(fn: () => void, timeout = 1_500): () => void {
  const idle = window.requestIdleCallback;
  if (idle) {
    const id = idle(fn, { timeout });
    return () => window.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(fn, 300);
  return () => window.clearTimeout(id);
}

function ignore<T>(promise: Promise<T>): void {
  promise.catch(() => {
    // Prewarm is best-effort. Route UI has its own error handling.
  });
}

export function preloadMiniappTab(tab: MiniappTab, walletAddress?: string): void {
  if (tab === 'tokens') {
    ignore(
      import('../routes/Home').then(({ warmHomeFeedCache }) =>
        Promise.all([
          warmHomeFeedCache({ sort: 'funded', status: 'active', page: 0, limit: 10 }),
          warmHomeFeedCache({ sort: 'funded', status: 'graduated', page: 0, limit: 10 }),
        ]),
      ),
    );
    return;
  }

  if (tab === 'fees') {
    ignore(import('../routes/Royalties').then(() => undefined));
    ignore(
      import('../hooks/useRoyalties').then(({ warmRoyaltiesCache }) =>
        warmRoyaltiesCache({ includeEmpty: false, limit: 12, offset: 0 }),
      ),
    );
    return;
  }

  ignore(import('../routes/Wallet').then(() => undefined));
  if (walletAddress) {
    ignore(
      import('../routes/Wallet').then(({ warmWalletPortfolioCache }) =>
        warmWalletPortfolioCache(walletAddress),
      ),
    );
  }
}

export function prewarmMiniappData(walletAddress?: string): () => void {
  const includeTest = import.meta.env.VITE_INCLUDE_TEST_TOKENS === 'true';
  return runIdle(() => {
    ignore(getBootstrap({ includeTest, limit: 10 }));
    preloadMiniappTab('tokens');
    // Fees and wallet can be expensive because they are user-specific and may
    // touch balances. Warm them on user intent (tab hover/tap), not on every app
    // open.
    void walletAddress;
  });
}
